import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { IShoppingCart, IShoppingCartItem, ServiceItem, ShoppingCartCoupon } from 'orderme-api-integration-client';
import { createShoppingCartsClient, ServiceClient } from '../../helpers/client';
import { RootState } from '../store';
import { round2 } from '@/helpers/costCounter';

export interface IShoppingCartWithServices extends IShoppingCart {
  services: ServiceItem[];
}

export interface IShoppingCartItemWithService extends IShoppingCartItem {
  serviceItem: ServiceItem;
}

export interface ShoppingCartState {
  cartId?: string;
  items: IShoppingCartItemWithService[];
  packs: IShoppingCartItemWithService[];
  priceToPay: number;
  totalPrice: number;
  totalOriginalPrice: number;
  loyaltyPrice: number;
  totalDiscount: number;
  couponDiscount: number;
  packsPrice: number;
  deliveryPrice: number;
  hasDelivery: boolean;
  coupon?: ShoppingCartCoupon;
  clientCouponCode?: string;
  usedCredits: number;
  updatingCart: boolean;
  onlyPhysicalItems: boolean;
  error?: string;
}

const initialState: ShoppingCartState = {
  cartId: null,
  items: [],
  packs: [],
  priceToPay: 0,
  totalPrice: 0,
  totalOriginalPrice: 0,
  loyaltyPrice: 0,
  totalDiscount: 0,
  couponDiscount: 0,
  deliveryPrice: 0,
  packsPrice: 0,
  hasDelivery: false,
  coupon: null,
  clientCouponCode: null,
  usedCredits: 0,
  updatingCart: false,
  onlyPhysicalItems: false,
  error: null,
};

const cartClient = createShoppingCartsClient();
const servicesClient = new ServiceClient();

// Normalize server cart into state
const applyCart = (state: ShoppingCartState, cart: IShoppingCartWithServices | null | undefined) => {
  if (!cart) {
    state.cartId = null;
    state.items = [];
    state.packs = [];
    state.priceToPay = 0;
    state.totalPrice = 0;
    state.totalOriginalPrice = 0;
    state.loyaltyPrice = 0;
    state.totalDiscount = 0;
    state.couponDiscount = 0;
    state.deliveryPrice = 0;
    state.packsPrice = 0;
    state.hasDelivery = false;
    state.onlyPhysicalItems = false;
    state.coupon = null;
    state.clientCouponCode = null;
    return;
  }

  state.cartId = cart.id;
  const servicesById = new Map(cart.services.map((s) => [s.id, s]));
  const items = (cart.items ?? []) as IShoppingCartItem[];
  const allItems = items
    .map((item) => {
      const service = item.serviceId ? servicesById.get(item.serviceId) : null;
      if (!service) {
        return null;
      }
      return { ...item, serviceItem: service } as IShoppingCartItemWithService;
    })
    .filter((i): i is IShoppingCartItemWithService => i !== null);

  // Separate regular items and packs
  state.items = allItems.filter((item) => !item.serviceItem.isPack);
  state.packs = allItems.filter((item) => item.serviceItem.isPack);

  var totalPrice = cart.totalPrice ?? 0;

  // Calculate total price of packs to exclude from calculations
  const packsPrice = round2(
    state.packs.reduce((sum, pack) => {
      const packPrice = pack.serviceItem.price * pack.count;
      return sum + packPrice;
    }, 0)
  );

  // Exclude packs price from total price
  if (packsPrice > 0) {
    totalPrice = round2(totalPrice - packsPrice);
  }
  state.packsPrice = packsPrice;

  const deliveryPrice = round2(cart.deliveryService?.totalPrice ?? 0);
  // Exclude delivery price from total price
  if (deliveryPrice > 0) {
    totalPrice = round2(totalPrice - deliveryPrice);
  }
  if (cart.totalCouponDiscount > 0) {
    totalPrice = round2(totalPrice + cart.totalCouponDiscount);
  }
  state.priceToPay = getPriceToPay(totalPrice, deliveryPrice, state.usedCredits, cart.totalCouponDiscount, packsPrice);
  state.totalPrice = totalPrice;
  const totalOriginalPrice = cart.totalOriginalPrice ?? 0;
  // Exclude delivery and packs price from total original price
  state.totalOriginalPrice = round2(totalOriginalPrice - deliveryPrice - packsPrice);
  state.deliveryPrice = deliveryPrice;
  state.hasDelivery = cart.deliveryService && true;
  state.coupon = cart.coupon;
  state.totalDiscount = round2(cart.totalDiscount - cart.totalCouponDiscount);
  state.couponDiscount = round2(cart.totalCouponDiscount);
  state.loyaltyPrice = round2(state.totalOriginalPrice - cart.totalLoyaltyDiscount);
  state.onlyPhysicalItems = state.items?.length > 0 && state.items.every((item) => item.serviceItem.isItem);
};

const fetchCartWithServices = async (customerId: string) => {
  const cart = await cartClient.get();
  const items = cart?.items ?? [];
  const serviceIds = items.map((i) => i.serviceId).filter((id, idx, arr) => arr.indexOf(id) === idx);

  let services: ServiceItem[] = [];
  if (serviceIds.length) {
    const fetched = await Promise.all(
      serviceIds.map(async (id) => {
        try {
          return (await servicesClient.getServiceById(id, customerId)) as ServiceItem;
        } catch {
          return null;
        }
      })
    );
    services = fetched.filter((s): s is ServiceItem => !!s);
  }

  return { ...cart, services } as IShoppingCartWithServices;
};

// Load cart from API
export const loadCartAsync = createAsyncThunk<IShoppingCartWithServices>('shoppingCart/load', async (_, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  var cart = await fetchCartWithServices(getCustomerId(state));
  const serverCode = cart?.coupon?.code?.toLowerCase() ?? null;
  const clientCode = state.shoppingCartState.clientCouponCode?.toLowerCase() ?? null;
  if (serverCode !== clientCode) {
    try {
      await cartClient.removeUsedCoupon();
    } catch (ex) {
      console.error('Not possible to reset coupon code.', ex);
      return;
    }

    // Reload cart after removing coupon code
    cart = await fetchCartWithServices(getCustomerId(state));
  }
  return cart;
});

// Add item → then fetch updated cart
export const addToCartAsync = createAsyncThunk<IShoppingCartWithServices, string>(
  'shoppingCart/add',
  async (serviceId, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const { cartId } = state.shoppingCartState;
    if (!cartId) {
      await cartClient.createCart();
    }

    await cartClient.addCartService(serviceId, 1);
    return await fetchCartWithServices(getCustomerId(state));
  }
);

// Remove all occurrences → then fetch updated cart
export const removeFromCartAsync = createAsyncThunk<IShoppingCartWithServices, string>(
  'shoppingCart/removeAll',
  async (serviceId, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    await cartClient.removeCartService(serviceId);
    return await fetchCartWithServices(getCustomerId(state));
  }
);

// Remove one occurrence → then fetch updated cart
export const removeOneFromCartAsync = createAsyncThunk<IShoppingCartWithServices, string>(
  'shoppingCart/removeOne',
  async (serviceId, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    await cartClient.removeCartService(serviceId, 1);
    return await fetchCartWithServices(getCustomerId(state));
  }
);

// Clear cart on server → then fetch updated cart
export const clearCartAsync = createAsyncThunk('shoppingCart/clearAll', async (_, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const { cartId } = state.shoppingCartState;
  if (cartId) {
    await cartClient.removeCart();
  }
});

const getCustomerId = (state: RootState) => state.customerState.customerInfo?.id;

const getPriceToPay = (
  totalPrice: number,
  deliveryPrice: number,
  usedCredits: number,
  discount: number,
  packs: number
) => {
  var priceToPay = round2(totalPrice + deliveryPrice - discount + packs);
  if (usedCredits > priceToPay) {
    return 0;
  }

  return round2(usedCredits > 0 ? priceToPay - usedCredits : priceToPay);
};

const shoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState,
  reducers: {
    payWithCredits: (state, action: PayloadAction<number>) => {
      state.usedCredits = action.payload;
      state.priceToPay = getPriceToPay(
        state.totalPrice,
        state.deliveryPrice,
        action.payload,
        state.couponDiscount,
        state.packsPrice
      );
    },
    clearPaymentWithCredits: (state) => {
      state.usedCredits = 0;
      state.priceToPay = getPriceToPay(
        state.totalPrice,
        state.deliveryPrice,
        0,
        state.couponDiscount,
        state.packsPrice
      );
    },
    assignCouponCode: (state, action: PayloadAction<string>) => {
      state.clientCouponCode = action.payload;
    },
    resetCouponCode: (state) => {
      state.clientCouponCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // load
      .addCase(loadCartAsync.pending, (state) => {
        state.updatingCart = true;
      })
      .addCase(loadCartAsync.fulfilled, (state, action) => {
        state.updatingCart = false;
        applyCart(state, action.payload);
      })
      .addCase(loadCartAsync.rejected, (state, _) => {
        state.updatingCart = false;
        applyCart(state, null);
      })

      // add
      .addCase(addToCartAsync.pending, (state) => {
        state.updatingCart = true;
      })
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        state.updatingCart = false;
        applyCart(state, action.payload);
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.updatingCart = false;
        state.error = action.error.message;
      })

      // remove all
      .addCase(removeFromCartAsync.pending, (state) => {
        state.updatingCart = true;
      })
      .addCase(removeFromCartAsync.fulfilled, (state, action) => {
        state.updatingCart = false;
        applyCart(state, action.payload);
      })
      .addCase(removeFromCartAsync.rejected, (state, action) => {
        state.updatingCart = false;
        state.error = action.error.message;
      })

      // remove one
      .addCase(removeOneFromCartAsync.pending, (state) => {
        state.updatingCart = true;
      })
      .addCase(removeOneFromCartAsync.fulfilled, (state, action) => {
        state.updatingCart = false;
        applyCart(state, action.payload);
      })
      .addCase(removeOneFromCartAsync.rejected, (state, action) => {
        state.updatingCart = false;
        state.error = action.error.message;
      })

      // clear
      .addCase(clearCartAsync.fulfilled, (state, _) => {
        state.updatingCart = false;
        applyCart(state, null);
        state.usedCredits = 0;
      })
      .addCase(clearCartAsync.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const { payWithCredits, clearPaymentWithCredits, assignCouponCode, resetCouponCode } = shoppingCartSlice.actions;
export default shoppingCartSlice.reducer;
